import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";

import {
  currenciesIndex,
  marketsIndex,
  pricesOtcIndex,
} from "@/services/swagger/gen";
import {
  CurrencyResource,
  MarketResource,
  OtcPriceResource,
} from "@/services/swagger/gen/models";

export const fetchConfig = createAsyncThunk(
  "config/fetch",
  async (_, { rejectWithValue }) => {
    try {
      const currencies = await currenciesIndex();

      const markets = await marketsIndex();

      return {
        currencies: currencies.data,
        markets: markets.data,
      }; // Adjust based on the structure of your API response
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to fetch currencies");
    }
  },
);

export const fetchPrices = createAsyncThunk(
  "config/fetchPrices",
  async (_, { rejectWithValue }) => {
    try {
      const prices = await pricesOtcIndex();

      return prices.data; // Adjust based on your API response
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to fetch prices");
    }
  },
);

export type CustomPriceResource = OtcPriceResource & {
  isPriceUnavailable: boolean;
};

interface IConfigState {
  currencyList: CurrencyResource[];
  marketList: MarketResource[];
  pricesList: CustomPriceResource[];
  status: "idle" | "loading" | "succeeded" | "failed";
  priceStatus: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
  showPrice: boolean;
  disabledFeatureFlags: Map<string, boolean>;
}

const initialState: IConfigState = {
  currencyList: [],
  marketList: [],
  status: "idle",
  priceStatus: "idle",
  error: null,
  pricesList: [],
  showPrice: true,
  disabledFeatureFlags: new Map(),
};

const configSlice = createSlice({
  name: "config",
  initialState,
  reducers: {
    setShowPrice: (state, action: PayloadAction<boolean>) => {
      state.showPrice = action.payload;
    },
    setDisabledFeatureFlags: (
      state,
      action: PayloadAction<Map<string, boolean>>,
    ) => {
      state.disabledFeatureFlags = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchConfig.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchConfig.fulfilled, (state, action) => {
        state.currencyList = action.payload.currencies;
        state.marketList = action.payload.markets;
      })
      .addCase(fetchConfig.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload as string;
      })
      .addCase(fetchPrices.pending, (state) => {
        state.priceStatus = "loading";
        state.error = null;
      })
      .addCase(fetchPrices.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.priceStatus = "succeeded";
        state.pricesList = action.payload.map((price: OtcPriceResource) => ({
          ...price,
          isPriceUnavailable: !price.signature, // Modify based on your logic
        }));
      })
      .addCase(fetchPrices.rejected, (state, action) => {
        state.status = "failed";
        state.priceStatus = "failed";
        state.error = action.payload as string;
      });
  },
});

export default configSlice.reducer;

export const { setShowPrice, setDisabledFeatureFlags } = configSlice.actions;
